import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTodayQueue } from '../queue/useTodayQueue';
import type { QueueItem } from '../queue/types';
// ConsultationChat is also embedded directly in the legacy VisitWorkflow's
// ConsultStage (components/stages/ConsultStage.jsx) — kept as the shared
// .jsx dependency of both rather than forked or converted here. It converts
// to TS on its own pass when ConsultStage/VisitWorkflow do (see CLAUDE.md's
// ABDM/UI sequencing note — this is the one deliberately deferred tree).
import ConsultationChat from '../../components/ConsultationChat';

interface ConsultationModuleProps {
  activeQueueItem?: { id: number } | null;
  onViewPatientRecord?: (uhid: string) => void;
  onOpenVisit?: (visitCode: string) => void;
}

export default function ConsultationModule({ activeQueueItem, onViewPatientRecord, onOpenVisit }: ConsultationModuleProps) {
  const { user } = useAuth();
  const queueQuery = useTodayQueue();
  const queue = queueQuery.data ?? [];
  const [selId, setSelId] = useState<number | null>(activeQueueItem?.id ?? null);

  // A doctor only sees patients assigned to them here — otherwise every
  // doctor would see the whole hospital's queue and could pick up someone
  // else's patient. Admin (and anyone else with this module) still sees
  // everyone, since they may need to oversee/reassign across doctors.
  // 'On Hold' belongs here too: an interrupted consultation is unfinished
  // business the doctor has to come back to, so it must stay visible in
  // their own list rather than disappearing until someone reopens the queue.
  const activeItems = queue.filter((q) => ['Waiting', 'In Consultation', 'On Hold'].includes(q.status)
    && (user?.role !== 'Doctor' || q.doctor_id === user.id));
  const patient: QueueItem | null = queue.find((q) => q.id === selId) || null;

  useEffect(() => { if (activeQueueItem) setSelId(activeQueueItem.id); }, [activeQueueItem]);

  // A queue row created through the normal check-in flow carries the exact
  // visit it belongs to (see routes/queue.js's GET /today) — redirect into
  // that visit's own Visit Workflow instead of the bare chat-only view
  // below, so the doctor lands on the same embedded Consultation stage the
  // rest of the stepper uses and can move on to OP/IP after approving a
  // document there, instead of getting stuck in this standalone view with
  // no way to advance. Only a queue row with no tracked visit (very old
  // data predating that column) falls back to the plain inline chat.
  useEffect(() => {
    if (patient?.visit_code && onOpenVisit) onOpenVisit(patient.visit_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, patient?.visit_code]);

  return (
    <div className="fade-in consult-grid" style={{ height: 'calc(100vh - 128px)' }}>

      {/* Patient sidebar */}
      <div className="card" style={{ overflow: 'auto', padding: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: '#6B7280', marginBottom: 10 }}>Active patients</div>

        {activeItems.length === 0
          ? <div style={{ textAlign: 'center', padding: 24, color: '#6B7280', fontSize: 13 }}>No active patients</div>
          : activeItems.map((item) => (
            <div key={item.id} onClick={() => setSelId(item.id)}
              style={{ padding: 10, borderRadius: 'var(--radius)', cursor: 'pointer', marginBottom: 8, background: selId === item.id ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${selId === item.id ? 'var(--teal)' : 'transparent'}`, transition: 'all .15s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{item.name}</div>
                <span className="badge badge-info" style={{ fontSize: 10 }}>{item.display_token ? `#${item.display_token}` : '—'}</span>
              </div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>{item.gender}, {item.age}y</div>
              {item.symptoms && (
                <div style={{ fontSize: 11, color: '#374151', marginTop: 5, fontStyle: 'italic' }}>"{item.symptoms.substring(0, 45)}{item.symptoms.length > 45 ? '…' : ''}"</div>
              )}
            </div>
          ))
        }
      </div>

      {/* Chat pane */}
      {!patient
        ? (
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 40 }}>🩺</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Select a patient to begin</div>
            <div style={{ fontSize: 13, color: '#6B7280' }}>Active queue patients appear on the left</div>
          </div>
        )
        : patient.visit_code
        ? (
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 40 }}>🔄</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Opening {patient.name}'s visit…</div>
          </div>
        )
        : (
          <ConsultationChat
            key={patient.id}
            queueItem={patient}
            extraContext={undefined}
            patientUhid={patient.uhid}
            onViewPatientRecord={onViewPatientRecord}
            onApproved={() => queueQuery.refetch()}
          />
        )
      }
    </div>
  );
}
